import React, { useState } from "react";
import {
  DeleteHallRoomPayload,
  HallRoom,
  UpdateHallRoomPayload,
} from "../../interfaces";
import { Button, Form, Popconfirm, Space, Table, TableProps, Tag } from "antd";
import { useForm } from "antd/lib/form/Form";
import { appSelector } from "../../helpers/appSelector";
import { useDispatch } from "react-redux";
import { DeleteOutlined } from "@ant-design/icons";
import { deleteHallRoom, updateHallRoom } from "../../store/hallRooms";
import UpdateHallRoomForm from "./UpdateHallRoomForm";

interface Props {
  hallRooms: HallRoom[];
  loading: boolean;
}

const HallRoomsList = ({ hallRooms, loading }: Props) => {
  const [floorId, setFloorId] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);
  const [form] = Form.useForm();

  const dispatch = useDispatch();
  const { success } = appSelector((state) => state.hallRoomStore);
  const { isHallRoomDeleting, deleteSuccess } = appSelector(
    (state) => state.hallRoomStore
  );

  const [deletableId, setDeletableId] = useState<string | null>(null);
  const [selectedRoom, setSelectedRoom] = useState<HallRoom>();

  const handleOnFinish = (values: UpdateHallRoomPayload) => {
    values.floor_id = selectedRoom?.floor?.id || "";
    values.id = selectedRoom?.id.toString() || "";
    dispatch(updateHallRoom(values));
    if (success) {
      setOpen(false);
    }
  };

  const handleDelete = (room: HallRoom) => {
    const payload: DeleteHallRoomPayload = {
      id: room.id,
      floor_id: room?.floor?.id || "",
      hall_id: room?.floor?.hall?.id || "",
    };
    dispatch(deleteHallRoom(payload));
    isHallRoomDeleting && setDeletableId(room.id);
    deleteSuccess && setDeletableId(null);
  };

  const columns: TableProps<HallRoom>["columns"] = [
    {
      title: "Room Name",
      dataIndex: "name",
      align: "left",
      key: "room",
      sorter: (a: HallRoom, b: HallRoom) => a.name.localeCompare(b.name),
    },
    {
      title: "Related Hall",
      align: "left",
      key: "hall",
      render: (record: HallRoom) => record.floor?.hall?.name,
    },
    {
      title: "Related Floor",
      align: "left",
      key: "floor",
      render: (record: HallRoom) => record.floor?.name,
    },
    {
      title: "Total Students",
      align: "left",
      key: "students",
      render: (record: HallRoom) => (
        <Tag color="success" key="room-count">
          {record.students?.length}
        </Tag>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      align: "left",
      key: "description",
      sorter: (a: HallRoom, b: HallRoom) =>
        (a.description?.length || 0) - (b.description?.length || 0),
    },
    // {
    //   title: "Total Beds",
    //   dataIndex: "total_beds",
    //   align: "center",
    //   key: "total",
    //   sorter: (a: any, b: any) => a.id - b.id,
    // },
    // {
    //   title: "Available Beds",
    //   dataIndex: "available_beds",
    //   align: "center",
    //   key: "available",
    //   sorter: (a: any, b: any) => a.id - b.id,
    // },
    // {
    //   title: "Occupied Beds",
    //   dataIndex: "occupied_beds",
    //   align: "center",
    //   key: "occupied",
    //   sorter: (a: any, b: any) => a.id - b.id,
    // },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (room: HallRoom) => (
        <Space size="middle">
          <Button
            key={`edit-${room.id}`}
            type="primary"
            onClick={() => {
              setSelectedRoom(room);
              setOpen(true);
            }}
          >
            Edit
          </Button>
          <Popconfirm
            placement="topLeft"
            title="Are you sure you want to delete this hallRoom?"
            onConfirm={() => {
              handleDelete(room);
            }}
            okText="Yes"
            cancelText="No"
          >
            <Button
              key={`delete-${room.id}`}
              danger
              icon={<DeleteOutlined />}
              loading={deletableId == room.id}
            >
              {deletableId == room.id ? "Deleting..." : "Delete"}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Table<HallRoom>
        size="small"
        columns={columns}
        dataSource={hallRooms}
        loading={loading}
        scroll={{ x: true }}
      />

      <UpdateHallRoomForm
        open={open}
        onCancel={() => {
          setOpen(false);
        }}
        form={form}
        onFinish={handleOnFinish}
        room={selectedRoom}
      />
    </>
  );
};

export default HallRoomsList;
